/**
 * Onboarding API Utilities
 *
 * Handles communication with the Raise AI API for onboarding form data
 */

import { useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://raiseai-api-production.up.railway.app';

/* ============================================================================
 * AUTOSAVE FEATURE FLAG
 * ----------------------------------------------------------------------------
 * Controls whether the legacy "save on every formData change (debounced)"
 * useEffect in each form page actually fires.
 *
 *   true  = legacy behavior — autosave on every keystroke (5s debounce).
 *           Generates 10s of Airtable writes per onboarding session.
 *   false = new "coalesced" behavior (DEFAULT) — saves only happen on:
 *             1. field blur (when a user tabs out of a field)
 *             2. page navigation (component unmount)
 *             3. tab close / refresh (beforeunload via sendBeacon)
 *             4. explicit Next button clicks (unchanged from before)
 *           Caps Airtable writes at roughly 1 per field exit, ~8-15 per
 *           full onboarding instead of dozens-to-hundreds.
 *
 * REVERT INSTRUCTIONS — if the new behavior misbehaves in production:
 *   - Flip this flag to `true` and redeploy. That re-enables the old
 *     autosave-on-change useEffect in every form page. The new
 *     useCoalescedSave hook continues running but becomes redundant
 *     (saves twice — once on blur, once on change). No harm, just extra
 *     writes until the old behavior is restored.
 *   - For a clean full revert, `git revert` the commit that introduced
 *     this flag.
 * ========================================================================== */
export const AUTOSAVE_ON_CHANGE = false;

/**
 * Get location ID from URL parameters or localStorage
 */
export function getLocationId() {
  // Check URL parameters first
  const params = new URLSearchParams(window.location.search);
  const locationIdFromUrl = params.get('locationId') || params.get('location_id');

  if (locationIdFromUrl) {
    // Store for future use
    localStorage.setItem('locationId', locationIdFromUrl);
    return locationIdFromUrl;
  }

  // Fall back to stored locationId
  return localStorage.getItem('locationId') || null;
}

/**
 * Get user email from URL parameters or localStorage
 */
export function getUserEmail() {
  // Check URL parameters first
  const params = new URLSearchParams(window.location.search);
  const emailFromUrl = params.get('email');

  if (emailFromUrl) {
    // Store for future use
    localStorage.setItem('userEmail', emailFromUrl);
    return emailFromUrl;
  }

  // Fall back to stored email
  return localStorage.getItem('userEmail') || null;
}

/**
 * Save form progress to API
 * @param {string} step - The form step name (e.g., 'deal-info', 'documents', etc.)
 * @param {object} data - The form data for this step
 * @returns {Promise<object>} API response
 */
export async function saveProgress(step, data) {
  const locationId = getLocationId();
  const email = getUserEmail();

  if (!locationId && !email) {
    throw new Error('Location ID or email required. Please ensure parameters are in URL.');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/save-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        locationId,  // Preferred lookup value
        email,       // Fallback for backward compatibility
        step,
        data
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save progress');
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving progress:', error);
    throw error;
  }
}

/**
 * Mark onboarding as complete
 * @returns {Promise<object>} API response
 */
export async function completeOnboarding() {
  const locationId = getLocationId();
  const email = getUserEmail();

  if (!locationId && !email) {
    throw new Error('Location ID or email required. Please ensure parameters are in URL.');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ locationId, email })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to complete onboarding');
    }

    return await response.json();
  } catch (error) {
    console.error('Error completing onboarding:', error);
    throw error;
  }
}

/**
 * Get onboarding status
 * @returns {Promise<object>} Status object with completed flag and form data
 */
export async function getOnboardingStatus() {
  const locationId = getLocationId();
  const email = getUserEmail();

  if (!locationId && !email) {
    throw new Error('Location ID or email required. Please ensure parameters are in URL.');
  }

  try {
    // Build query string with available parameters
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    if (email) params.append('email', email);

    const response = await fetch(`${API_BASE_URL}/api/onboarding/status?${params.toString()}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get onboarding status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    throw error;
  }
}

/**
 * Load saved form data from API
 * @param {string} step - The form step name to load data for
 * @returns {Promise<object>} Form data for the specified step
 */
export async function loadFormData(step) {
  try {
    const status = await getOnboardingStatus();

    if (status.formData && status.formData[step]) {
      return status.formData[step];
    }

    return {};
  } catch (error) {
    console.error('Error loading form data:', error);
    return {};
  }
}

/* ============================================================================
 * useCoalescedSave — new save strategy (default)
 * ----------------------------------------------------------------------------
 * Replaces the old "save on every formData change" pattern.
 *
 * The hook does THREE things:
 *   1. Attaches a beforeunload listener that fires a synchronous save via
 *      navigator.sendBeacon() when the user closes the tab or refreshes.
 *      This is the safety net that prevents data loss from the new design.
 *   2. On component unmount (i.e., the user navigates to the next/prev page
 *      inside the SPA), fires a normal saveProgress() call. This catches
 *      every page transition.
 *   3. Returns a `handleBlur` handler. The caller attaches it to the form's
 *      wrapper element (a single onBlur on the parent — React event bubbling
 *      means it fires when ANY child input loses focus). Each blur triggers
 *      a saveProgress call.
 *
 * Inputs:
 *   step         - same step identifier used by saveProgress (e.g. 'business-info')
 *   buildPayload - a function returning the data object to save. Defined by
 *                  the caller so each page controls its own shape. Called
 *                  fresh on every save so it always sees the latest formData.
 *   isLoading    - boolean; when true (during initial load), all saves are
 *                  suppressed so we don't overwrite the just-loaded data
 *                  with an empty payload.
 *
 * Return:
 *   { handleBlur } — onBlur handler to attach to the form wrapper.
 *
 * REVERT NOTE: if this hook causes problems, set AUTOSAVE_ON_CHANGE = true
 * above. The hook will keep running but be functionally redundant.
 * ========================================================================== */
export function useCoalescedSave({ step, buildPayload, isLoading }) {
  // Keep a ref to the latest buildPayload so listeners registered once at
  // mount can still see fresh formData. Without this, the beforeunload
  // handler would capture a stale closure from the first render.
  const buildPayloadRef = useRef(buildPayload);
  const isLoadingRef = useRef(isLoading);
  useEffect(() => {
    buildPayloadRef.current = buildPayload;
    isLoadingRef.current = isLoading;
  });

  // Effect 1: register beforeunload listener (tab close / refresh).
  // Uses navigator.sendBeacon — a fire-and-forget POST that the browser
  // guarantees will be sent even as the page is unloading. fetch() won't
  // reliably complete during unload.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isLoadingRef.current) return;
      try {
        const locationId = getLocationId();
        const email = getUserEmail();
        if (!locationId && !email) return;
        const body = JSON.stringify({
          locationId,
          email,
          step,
          data: buildPayloadRef.current()
        });
        // sendBeacon takes a Blob; setting the type ensures the server
        // parses it as JSON (same as the fetch path).
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE_URL}/api/onboarding/save-progress`, blob);
      } catch (err) {
        // Swallow — we're unloading anyway and there's nothing useful to do.
        console.error('beforeunload save failed:', err);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [step]);

  // Effect 2: fire a final save when the component unmounts (SPA navigation
  // to next/prev step). Runs once on unmount via the cleanup function.
  useEffect(() => {
    return () => {
      if (isLoadingRef.current) return;
      // Fire-and-forget. We don't await because the component is unmounting.
      saveProgress(step, buildPayloadRef.current()).catch(err => {
        console.error(`Unmount save failed for ${step}:`, err);
      });
    };
  }, [step]);

  // The blur handler. Attached via onBlur on the form's wrapper element so
  // React bubbling delivers blur events from every input inside it. We
  // debounce a tiny amount (250ms) to coalesce rapid focus-shift events
  // (e.g., tabbing through fields quickly) into a single save.
  const blurTimerRef = useRef(null);
  const handleBlur = () => {
    if (isLoadingRef.current) return;
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      saveProgress(step, buildPayloadRef.current()).catch(err => {
        console.error(`Blur save failed for ${step}:`, err);
        // Same localStorage fallback as the old autosave on API error.
        try {
          localStorage.setItem(`formData_${step}_backup`, JSON.stringify(buildPayloadRef.current()));
        } catch (_) { /* ignore quota errors */ }
      });
    }, 250);
  };

  return { handleBlur };
}

/**
 * Hook-style wrapper for auto-saving form data
 * Returns save function and loading state
 *
 * LEGACY: superseded by useCoalescedSave when AUTOSAVE_ON_CHANGE === false.
 * Kept for backward compatibility and easy revert.
 */
export function useAutoSave(step) {
  const saveWithDebounce = (data) => {
    // Clear any existing timeout
    if (window._autoSaveTimeout) {
      clearTimeout(window._autoSaveTimeout);
    }

    // Set new timeout
    window._autoSaveTimeout = setTimeout(() => {
      saveProgress(step, data)
        .then(() => {
          console.log(`Auto-saved ${step} data`);
        })
        .catch(error => {
          console.error(`Failed to auto-save ${step}:`, error);
          // Fall back to localStorage on error
          localStorage.setItem(`formData_${step}`, JSON.stringify(data));
        });
    }, 5000); // 5 second debounce
  };

  return { saveWithDebounce };
}
